import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchQuery } from "react-relay";

import blocExistsQuery from "./BlocExistsQuery";
import EditBlocModal from "./EditBlocModal";

import AdvancedReportPage from "pstat-anywhere/components/pages/reports/AdvancedReportPage";
import { environment } from "pstat-anywhere/relay";
import colors from "pstat-anywhere/themes/policystat/colors";
import { getRootUrl } from "pstat-anywhere/utils/urls";
import { InfoBanner } from "pstat-design-system/banner";
import { InfoExclamationIcon } from "pstat-design-system/banner/BannerIcons";
import LoadingSpinner from "pstat-design-system/LoadingSpinner";
import { Button } from "pstat-design-system/shared/Button";
import { Text } from "pstat-design-system/text";


const InfoText = props => (
  <Fragment>
    <InfoExclamationIcon />
    <Text id={props.id} color="nav.100" fontSize={props.fontSize}>
      {props.children}
    </Text>
  </Fragment>
);

export const ActionPanel = ({
  selectedTenantPks,
  existingBlocsCache,
  initiallySelectedPks,
  onSelectTenants,
  blocName,
  documentSettings,
  selectedTenantsChanged,
  router
}) => {
  const { t } = useTranslation();
  const [checkingForDuplicates, setCheckingForDuplicates] = useState(false);
  const [foundDuplicate, setFoundDuplicate] = useState(false);
  const [duplicateBlocName, setDuplicateBlocName] = useState(null);
  const atLeastTwoSitesSelected = selectedTenantPks.length >= 2;

  const getCacheKey = selectedTenantPks => {
    const selectedTenantPksCopy = [...selectedTenantPks];
    selectedTenantPksCopy.sort();
    return selectedTenantPksCopy.join("-");
  };

  const handleExistingBlocCheck = bloc => {
    if (bloc) {
      setFoundDuplicate(true);
      setDuplicateBlocName(bloc.name);
    }
  };

  useEffect(() => {
    if (atLeastTwoSitesSelected) {
      setFoundDuplicate(false);
      const cacheKey = getCacheKey(selectedTenantPks);
      if (existingBlocsCache.hasOwnProperty(cacheKey)) {
        const cachedBloc = existingBlocsCache[cacheKey];
        handleExistingBlocCheck(cachedBloc);
        return;
      }
      setCheckingForDuplicates(true);
      const variables = {
        tenantPks: selectedTenantPks
      };
      fetchQuery(environment, blocExistsQuery, variables).then(data => {
        const bloc = data.blocExists.bloc;
        handleExistingBlocCheck(bloc);
        existingBlocsCache[cacheKey] = bloc;
        setCheckingForDuplicates(false);
      });
    }
  }, [selectedTenantPks, atLeastTwoSitesSelected, existingBlocsCache]);
  return (
    <Fragment>
      {atLeastTwoSitesSelected ? (
        <Fragment>
          {checkingForDuplicates ? (
            <LoadingSpinner
              size={20}
              colorOne={colors["nav"][100]}
              colorTwo={colors["nav"][100]}
            />
          ) : (
            <Fragment>
              {foundDuplicate && selectedTenantsChanged(selectedTenantPks) ? (
                <InfoText id="duplicate-bloc-message" fontSize={1}>
                  {t("bloc.sites.duplicateMessage", {
                    blocName: duplicateBlocName
                  })}
                </InfoText>
              ) : (
                <Fragment>
                  {initiallySelectedPks.length > 0 ? (
                    <Button
                      id="update-selected-tenants-button"
                      buttonStyle="primary"
                      onClick={() => onSelectTenants(selectedTenantPks)}
                    >
                      {t("bloc.details.updateSites", { blocName })}
                    </Button>
                  ) : (
                    <CreateBlocButton
                      selectedTenantPks={selectedTenantPks}
                      documentSettings={documentSettings}
                      router={router}
                    />
                  )}
                </Fragment>
              )}
            </Fragment>
          )}
        </Fragment>
      ) : (
        <InfoText>{t("bloc.sites.atLeastTwoSitesMessage")}</InfoText>
      )}
    </Fragment>
  );
};

const CreateBlocButton = ({ selectedTenantPks, documentSettings, router }) => {
  const { t } = useTranslation();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  return (
    <Fragment>
      <Button
        id="create-new-bloc-button"
        buttonStyle="primary"
        onClick={() => setModalIsOpen(true)}
      >
        {t("bloc.sites.createBlocButton")}
      </Button>
      <EditBlocModal
        isOpen={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        currentBlocName={""}
        documentSettings={documentSettings}
        currentDocumentSettingsPk={null}
        selectedTenantPks={selectedTenantPks}
        onViewOrEditSitesClicked={() => setModalIsOpen(false)}
        isNewBloc={true}
        selectedTenantsChanged={selectedTenantPks => true}
        router={router}
      />
    </Fragment>
  );
};

const BlocManagement = ({
  location,
  variables,
  route,
  router,
  data,
  tableType,
  existingBlocsCache,
  documentSettings
}) => {
  const { t } = useTranslation();
  const tabs = [
    {
      id: "blocs_manage",
      textKey: "bloc.manage.blocsTab",
      path: "bloc"
    },
    {
      id: "bloc_tenants",
      textKey: "bloc.manage.sitesTab",
      path: "bloc/sites"
    }
  ];
  return (
    <AdvancedReportPage
      variables={variables}
      data={data}
      route={route}
      router={router}
      location={location}
      tabs={tabs}
      headerText={t("bloc.manage.heading")}
      renderHeader={() => (
        <Fragment>
          {route && route.data.routeName !== "bloc_tenants" && (
            <Button
              id="create-new-bloc-button"
              buttonStyle="secondary"
              onClick={() => router.push(`${getRootUrl()}/bloc/sites`)}
            >
              {t("bloc.sites.createBlocButton")}
            </Button>
          )}
        </Fragment>
      )}
      noContentMessage={t("bloc.manage.emptyMessage")}
      showTabsPanel={true}
      tableType={tableType}
      showPagination={false}
      customOnRow={() => {}}
      frozenColumnTable={true}
      showActionButtonPanel={true}
      renderActionPanel={selectedSitePks => (
        <ActionPanel
          selectedTenantPks={selectedSitePks}
          existingBlocsCache={existingBlocsCache}
          initiallySelectedPks={[]}
          documentSettings={documentSettings}
          selectedTenantsChanged={selectedTenantPks => true}
          router={router}
        />
      )}
      singularSelectedActionsLabel={t("bloc.sites.singularSitesLabel")}
      pluralSelectedActionsLabel={t("bloc.sites.pluralSitesLabel")}
    />
  );
};

export const BlocManagementPage = ({ blocs, ...otherProps }) => {
  return (
    <BlocManagement
      data={blocs?.blocs}
      tableType="blocs_management"
      {...otherProps}
    />
  );
};

export const BlocTenantsPage = ({ canAdminTenants, ...otherProps }) => {
  const { t } = useTranslation();
  const existingBlocsCache = useRef({});
  return (
    <Fragment>
      <InfoBanner message={t("bloc.sites.bannerMessage")} width="100%" />
      <BlocManagement
        data={canAdminTenants?.tenants}
        tableType="bloc_tenants"
        existingBlocsCache={existingBlocsCache.current}
        documentSettings={canAdminTenants?.documentSettings}
        {...otherProps}
      />
    </Fragment>
  );
};
